import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Grid,
  GridItem,
  Heading,
  Text,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  useColorModeValue,
  Spinner,
  Center,
  Alert,
  AlertIcon,
  Icon,
  VStack,
  HStack,
  Divider,
  Badge,
  SimpleGrid,
} from "@chakra-ui/react";
import {
  BsPeople,
  BsPersonCheck,
  BsCalendarCheck,
  BsBriefcase,
  BsGraphUp,
  BsGraphDown,
  BsClipboardCheck,
  BsPersonPlus,
} from "react-icons/bs";
import axios from "axios";
import { BASE_URL } from "../services/api.js";
import api from "../services/api.js";

const StatCard = ({ title, value, icon, helpText, trend, isLoading }) => {
  const bg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  return (
    <Box
      bg={bg}
      p={6}
      borderRadius="xl"
      borderWidth="1px"
      borderColor={borderColor}
      boxShadow="sm"
      _hover={{ boxShadow: "md", transform: "translateY(-2px)" }}
      transition="all 0.2s"
    >
      {isLoading ? (
        <Center h="100px">
          <Spinner size="md" />
        </Center>
      ) : (
        <Stat>
          <HStack justify="space-between" mb={2}>
            <StatLabel fontSize="sm" fontWeight="medium">
              {title}
            </StatLabel>
            <Icon as={icon} boxSize={6} color="teal.500" />
          </HStack>
          <StatNumber fontSize="3xl" fontWeight="bold">
            {value}
          </StatNumber>
          {helpText && (
            <StatHelpText mb={0}>
              {trend && <StatArrow type={trend} />}
              {helpText}
            </StatHelpText>
          )}
        </Stat>
      )}
    </Box>
  );
};

const SectionCard = ({ title, children, isLoading }) => {
  const bg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  return (
    <Box
      bg={bg}
      p={6}
      borderRadius="xl"
      borderWidth="1px"
      borderColor={borderColor}
      boxShadow="sm"
      h="full"
    >
      <Heading size="md" mb={4}>
        {title}
      </Heading>
      {isLoading ? (
        <Center h="200px">
          <Spinner size="lg" />
        </Center>
      ) : (
        children
      )}
    </Box>
  );
};

const Dashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [headcountData, setHeadcountData] = useState(null);
  const [recruitmentData, setRecruitmentData] = useState(null);
  const [attendanceData, setAttendanceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      // Fetch all analytics in parallel using the configured api instance
      // This ensures 401 interceptor will be triggered
      const [dashboardRes, headcountRes, recruitmentRes, attendanceRes] =
        await Promise.all([
          api.get(`/api/analytics/dashboard`),
          api.get(`/api/analytics/headcount`),
          api.get(`/api/analytics/recruitment`),
          api.get(`/api/analytics/attendance`),
        ]);

      setDashboardData(dashboardRes.data.data);
      setHeadcountData(headcountRes.data.data);
      setRecruitmentData(recruitmentRes.data.data);
      setAttendanceData(attendanceRes.data.data);
      setError(null);
    } catch (err) {
      console.error("Error fetching dashboard data:", err);
      setError(err.response?.data?.message || "Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const bgColor = useColorModeValue("gray.50", "gray.900");

  if (error) {
    return (
      <Container maxW="container.xl" py={8}>
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Box bg={bgColor} minH="100vh" py={8}>
      <Container maxW="container.xl">
        <VStack spacing={8} align="stretch">
          {/* Header */}
          <Box>
            <Heading size="xl" mb={2}>
              HR Analytics Dashboard
            </Heading>
            <Text color="gray.600" fontSize="lg">
              Real-time insights into your workforce
            </Text>
          </Box>

          {/* Key Metrics - Top Row */}
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
            <StatCard
              title="Total Employees"
              value={dashboardData?.headcount?.total || 0}
              icon={BsPeople}
              helpText={`${dashboardData?.headcount?.active || 0} active`}
              isLoading={loading}
            />
            <StatCard
              title="New Hires (This Month)"
              value={dashboardData?.recruitment?.newHires || 0}
              icon={BsPersonPlus}
              trend="increase"
              helpText="Compared to last month"
              isLoading={loading}
            />
            <StatCard
              title="Attendance Rate"
              value={`${dashboardData?.attendance?.rate || 0}%`}
              icon={BsCalendarCheck}
              helpText={`${
                dashboardData?.attendance?.present || 0
              } present today`}
              isLoading={loading}
            />
            <StatCard
              title="Open Positions"
              value={dashboardData?.recruitment?.openPositions || 0}
              icon={BsBriefcase}
              helpText={`${
                dashboardData?.recruitment?.activeApplications || 0
              } active applications`}
              isLoading={loading}
            />
          </SimpleGrid>

          {/* Secondary Metrics */}
          <Grid templateColumns={{ base: "1fr", lg: "repeat(2, 1fr)" }} gap={6}>
            {/* Headcount by Department */}
            <SectionCard title="Headcount by Department" isLoading={loading}>
              <VStack spacing={3} align="stretch">
                {headcountData?.byDepartment?.slice(0, 5).map((dept, idx) => (
                  <HStack key={idx} justify="space-between">
                    <Text fontWeight="medium">{dept._id || "Unassigned"}</Text>
                    <Badge colorScheme="teal" fontSize="md" px={3} py={1}>
                      {dept.count}
                    </Badge>
                  </HStack>
                ))}
                {!headcountData?.byDepartment?.length && (
                  <Text color="gray.500" textAlign="center" py={8}>
                    No department data available
                  </Text>
                )}
              </VStack>
            </SectionCard>

            {/* Headcount by Location */}
            <SectionCard title="Headcount by Location" isLoading={loading}>
              <VStack spacing={3} align="stretch">
                {headcountData?.byLocation?.slice(0, 5).map((loc, idx) => (
                  <HStack key={idx} justify="space-between">
                    <Text fontWeight="medium">{loc._id || "Unassigned"}</Text>
                    <Badge colorScheme="blue" fontSize="md" px={3} py={1}>
                      {loc.count}
                    </Badge>
                  </HStack>
                ))}
                {!headcountData?.byLocation?.length && (
                  <Text color="gray.500" textAlign="center" py={8}>
                    No location data available
                  </Text>
                )}
              </VStack>
            </SectionCard>
          </Grid>

          {/* Recruitment Pipeline */}
          <SectionCard title="Recruitment Pipeline" isLoading={loading}>
            <Grid
              templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }}
              gap={6}
            >
              <Box textAlign="center" p={4} borderRadius="lg" bg="blue.50">
                <Icon as={BsBriefcase} boxSize={8} color="blue.500" mb={2} />
                <Text fontSize="2xl" fontWeight="bold" color="blue.700">
                  {recruitmentData?.activeJobs || 0}
                </Text>
                <Text fontSize="sm" color="gray.600">
                  Active Job Postings
                </Text>
              </Box>
              <Box textAlign="center" p={4} borderRadius="lg" bg="teal.50">
                <Icon as={BsPersonCheck} boxSize={8} color="teal.500" mb={2} />
                <Text fontSize="2xl" fontWeight="bold" color="teal.700">
                  {recruitmentData?.pipeline?.total || 0}
                </Text>
                <Text fontSize="sm" color="gray.600">
                  Total Applications
                </Text>
              </Box>
              <Box textAlign="center" p={4} borderRadius="lg" bg="green.50">
                <Icon as={BsGraphUp} boxSize={8} color="green.500" mb={2} />
                <Text fontSize="2xl" fontWeight="bold" color="green.700">
                  {recruitmentData?.pipeline?.conversionRate || 0}%
                </Text>
                <Text fontSize="sm" color="gray.600">
                  Conversion Rate
                </Text>
              </Box>
            </Grid>

            <Divider my={4} />

            <VStack spacing={3} align="stretch">
              <Text fontWeight="semibold" fontSize="sm" color="gray.600">
                Applications by Stage
              </Text>
              {recruitmentData?.pipeline?.byStage?.map((stage, idx) => (
                <HStack key={idx} justify="space-between">
                  <Text textTransform="capitalize">
                    {stage._id?.replace(/_/g, " ")}
                  </Text>
                  <Badge colorScheme="purple" fontSize="sm" px={2}>
                    {stage.count}
                  </Badge>
                </HStack>
              ))}
            </VStack>
          </SectionCard>

          {/* Attendance Summary */}
          <Grid templateColumns={{ base: "1fr", lg: "repeat(2, 1fr)" }} gap={6}>
            <SectionCard title="Attendance Summary" isLoading={loading}>
              <VStack spacing={4} align="stretch">
                <Box p={4} bg="green.50" borderRadius="lg">
                  <HStack justify="space-between">
                    <Text fontWeight="semibold">Overall Attendance</Text>
                    <Text fontSize="2xl" fontWeight="bold" color="green.600">
                      {attendanceData?.summary?.attendanceRate || 0}%
                    </Text>
                  </HStack>
                </Box>
                <SimpleGrid columns={2} spacing={4}>
                  <Box textAlign="center" p={3} bg="blue.50" borderRadius="md">
                    <Text fontSize="xl" fontWeight="bold" color="blue.600">
                      {attendanceData?.summary?.totalPresent || 0}
                    </Text>
                    <Text fontSize="xs" color="gray.600">
                      Present
                    </Text>
                  </Box>
                  <Box textAlign="center" p={3} bg="gray.100" borderRadius="md">
                    <Text fontSize="xl" fontWeight="bold" color="gray.600">
                      {attendanceData?.summary?.expected || 0}
                    </Text>
                    <Text fontSize="xs" color="gray.600">
                      Expected
                    </Text>
                  </Box>
                </SimpleGrid>
              </VStack>
            </SectionCard>

            <SectionCard title="Quick Actions" isLoading={false}>
              <VStack spacing={3} align="stretch">
                <Box
                  p={4}
                  borderRadius="lg"
                  borderWidth="1px"
                  borderColor="gray.200"
                  _hover={{ bg: "gray.50", cursor: "pointer" }}
                >
                  <HStack>
                    <Icon as={BsClipboardCheck} color="teal.500" />
                    <Text fontWeight="medium">
                      Pending Leaves: {dashboardData?.leaves?.pending || 0}
                    </Text>
                  </HStack>
                </Box>
                <Box
                  p={4}
                  borderRadius="lg"
                  borderWidth="1px"
                  borderColor="gray.200"
                  _hover={{ bg: "gray.50", cursor: "pointer" }}
                >
                  <HStack>
                    <Icon as={BsBriefcase} color="blue.500" />
                    <Text fontWeight="medium">
                      Active Recruitments:{" "}
                      {dashboardData?.recruitment?.activeApplications || 0}
                    </Text>
                  </HStack>
                </Box>
                <Box
                  p={4}
                  borderRadius="lg"
                  borderWidth="1px"
                  borderColor="gray.200"
                  _hover={{ bg: "gray.50", cursor: "pointer" }}
                >
                  <HStack>
                    <Icon as={BsPeople} color="purple.500" />
                    <Text fontWeight="medium">
                      Active Employees: {dashboardData?.headcount?.active || 0}
                    </Text>
                  </HStack>
                </Box>
              </VStack>
            </SectionCard>
          </Grid>
        </VStack>
      </Container>
    </Box>
  );
};

export default Dashboard;
